import { validateContentReference } from '@gitbook/app-content-refs';
import {
    Block,
    type Change,
    Fragment,
    NODE_MISSING_FRAGMENT,
    PARENT_OBJECT_INVALID,
    PARENT_TYPE_INVALID,
    Schema,
} from '@gitbook/slate';

import { createParagraph } from '../paragraph';
import {
    BLOCK_COLUMN,
    BLOCK_IF,
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_PARAGRAPH,
    BLOCK_STEPPER_STEP,
    BLOCK_TABS_ITEM,
    FRAGMENT_CAPTION,
    FRAGMENT_EXPANDABLE_BODY,
    INLINE_IMAGE,
    INLINE_LINK,
    LEAF_RICH_BLOCKS,
    LIST_BLOCKS,
} from '../types';
import { createImageInline } from './utils';

const validateImageRef = validateContentReference(['file', 'url']);

/**
 * Schema to validate images.
 */
export const schema = Schema.create({
    validations: [validateImagesBlockWithFrame],
    inlines: {
        [INLINE_IMAGE]: {
            isVoid: true,
            parent: { types: [...LEAF_RICH_BLOCKS, INLINE_LINK] },
            data: {
                size: (size) => [undefined, 'line', 'original'].includes(size),
                ref: validateImageRef,
                refDark: (ref) => ref === undefined || validateImageRef(ref),
                caption: (caption) => typeof caption === 'undefined' || typeof caption === 'string',
                alt: (alt) => typeof alt === 'undefined' || typeof alt === 'string',
            },
        },
    },
    blocks: {
        [BLOCK_IMAGES]: {
            parent: {
                objects: ['document', 'block', 'fragment'],
                types: [
                    undefined,
                    BLOCK_TABS_ITEM,
                    ...LIST_BLOCKS,
                    BLOCK_LIST_ITEM,
                    BLOCK_STEPPER_STEP,
                    FRAGMENT_EXPANDABLE_BODY,
                    BLOCK_COLUMN,
                    BLOCK_IF,
                ],
            },
            nodes: [{ types: [BLOCK_IMAGE], min: 1 }],
            isVoid: false,
            data: {
                align: (align) =>
                    align === undefined ||
                    align === 'left' ||
                    align === 'right' ||
                    align === 'center',
                withFrame: (withFrame) =>
                    typeof withFrame === 'undefined' || typeof withFrame === 'boolean',
            },
            normalize: (change, violation, { node }) => {
                switch (violation) {
                    case PARENT_OBJECT_INVALID:
                    case PARENT_TYPE_INVALID: {
                        // Convert to an inline images in a paragraph
                        const paragraph = Block.create({
                            type: BLOCK_PARAGRAPH,
                            // @ts-expect-error
                            nodes: node.nodes.map((child) =>
                                createImageInline(child.data.get('ref'), {
                                    caption: child.fragments.get(FRAGMENT_CAPTION)?.text,
                                    size: 'original',
                                })
                            ),
                        });

                        // @ts-expect-error
                        return change.replaceNodeByKey(node.key, paragraph);
                    }
                }
            },
        },
        [BLOCK_IMAGE]: {
            parent: { types: [BLOCK_IMAGES] },
            isVoid: true,
            data: {
                ref: validateImageRef,
                refDark: (ref) => ref === undefined || validateImageRef(ref),
                alt: (alt) => typeof alt === 'undefined' || typeof alt === 'string',
                fullWidth: (fullWidth) =>
                    typeof fullWidth === 'undefined' || typeof fullWidth === 'boolean',
            },
            fragments: {
                keys: [FRAGMENT_CAPTION],
                nodes: [{ types: [BLOCK_PARAGRAPH], min: 1, max: 1 }],
            },
            normalize: (change, violation, { node }) => {
                switch (violation) {
                    case NODE_MISSING_FRAGMENT: {
                        return change.setFragmentByKey(
                            // @ts-expect-error
                            node.key,
                            Fragment.create({
                                fragment: FRAGMENT_CAPTION,
                                nodes: [createParagraph()],
                            })
                        );
                    }
                    case PARENT_TYPE_INVALID: {
                        // @ts-expect-error
                        return change.wrapBlockByKey(node.key, {
                            type: BLOCK_IMAGES,
                            isVoid: true,
                        });
                    }
                }
            },
        },
    },
});

/**
 * Rule to remove the `withFrame` option from the images block
 * when there is more than 1 image block child.
 */
function validateImagesBlockWithFrame(node: Block): undefined | ((change: Change) => void) {
    if (node.object !== 'block' || node.type !== BLOCK_IMAGES) {
        return;
    }

    const hasFrame = Boolean(node.data.get('withFrame'));
    const hasMultipleChildren = node.nodes.size > 1;
    if (!hasFrame || !hasMultipleChildren) {
        return;
    }

    return (change) => {
        change.removeDataByKey(node.key, ['withFrame']);
    };
}
